import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getUpgradePath } from '../../utils/paths';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredTier?: 'free' | 'pro' | 'enterprise';
}

const TIER_HIERARCHY: Record<string, number> = {
  free: 1,
  pro: 2,
  enterprise: 3,
};

export function ProtectedRoute({ children, requiredTier }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check subscription tier if required
  if (requiredTier && user) {
    const userTierLevel = TIER_HIERARCHY[user.subscription_tier] || 0;
    const requiredTierLevel = TIER_HIERARCHY[requiredTier] || 0;

    // For past_due users, still allow access to paid features during grace period
    // but the UI will show a warning (handled in Settings.tsx)
    const isPastDue = user.subscription_status === 'past_due';
    const isUnpaid = user.subscription_status === 'unpaid' || user.subscription_status === 'cancelled';

    const upgradePath = getUpgradePath();

    // If subscription is completely cancelled/unpaid, downgrade access
    if (isUnpaid && userTierLevel < requiredTierLevel) {
      return <Navigate to={upgradePath} replace />;
    }

    // For active or past_due (grace period), check normal tier hierarchy
    if (!isPastDue && userTierLevel < requiredTierLevel) {
      // Redirect to upgrade page or show access denied
      return <Navigate to={upgradePath} replace />;
    }
  }

  return <>{children}</>;
}
